import React, { useMemo, useState } from "react";
import { Autocomplete, CircularProgress, TextField } from "@mui/material";
import axios from "axios";
import { BASE_URL } from "../../../../Utlils";

export const buildQuestionOptions = (
  allQuestions,
  questionType,
  existingQualifications,
  isEdit
) => {
  const seen = new Set();
  return (allQuestions || [])
    .filter((question) => {
      if (questionType === "Single Punch") {
        return (
          question?.questionType === "Single Punch" ||
          question?.questionType === "Dummy"
        );
      }
      return question?.questionType === questionType;
    })
    .filter((question) => {
      if (isEdit) return true;
      return !existingQualifications?.some(
        (qualification) => qualification?.question === question?._id
      );
    })
    .filter((question) => {
      if (!question?._id || seen.has(question._id)) return false;
      seen.add(question._id);
      return true;
    })
    .map((question) => ({
      label: question?.questionText || "",
      value: question._id,
      questionId: question?.questionId,
    }));
};

const QuestionAutocomplete = ({
  selectedQuestion,
  options,
  onSelect,
  placeholder = "Search by question text or ID",
}) => {
  const value = useMemo(() => {
    if (!selectedQuestion?._id) return null;
    return (
      options.find((option) => option.value === selectedQuestion._id) || {
        label: selectedQuestion?.questionText || "",
        value: selectedQuestion._id,
        questionId: selectedQuestion?.questionId,
      }
    );
  }, [options, selectedQuestion]);

  const autocompleteOptions = useMemo(() => {
    if (!value) return options;
    if (options.some((option) => option.value === value.value)) return options;
    return [value, ...options];
  }, [options, value]);

  return (
    <Autocomplete
      fullWidth
      options={autocompleteOptions}
      value={value}
      onChange={(_, newValue) => onSelect(newValue || null)}
      isOptionEqualToValue={(option, optionValue) =>
        option?.value === optionValue?.value
      }
      getOptionLabel={(option) => {
        if (!option) return "";
        if (typeof option === "string") return option;
        return option.label || "";
      }}
      filterOptions={(opts, state) => {
        const input = state.inputValue?.toLowerCase().trim();
        if (!input) return opts;
        return opts.filter(
          (option) =>
            option.label?.toLowerCase().includes(input) ||
            String(option.questionId || "")
              .toLowerCase()
              .includes(input)
        );
      }}
      renderOption={(props, option) => {
        const { key, ...optionProps } = props;
        return (
          <li key={option.value || key} {...optionProps}>
            <div className="flex flex-col gap-0.5 py-0.5">
              {option.questionId ? (
                <span className="text-[11px] text-[#666] font-[500]">
                  #{option.questionId}
                </span>
              ) : null}
              <span className="text-[13px] text-[#333] leading-snug whitespace-normal">
                {option.label}
              </span>
            </div>
          </li>
        );
      }}
      renderInput={(params) => (
        <TextField {...params} placeholder={placeholder} />
      )}
      noOptionsText="No matching questions"
      autoHighlight
      blurOnSelect
      ListboxProps={{
        style: { maxHeight: 280 },
      }}
      componentsProps={{
        popper: {
          sx: { zIndex: 2000 },
          placement: "bottom-start",
        },
        paper: {
          sx: {
            boxShadow: "0px 8px 24px rgba(0, 0, 0, 0.12)",
            border: "1px solid #E0E0E0",
          },
        },
      }}
      sx={{
        "& .MuiOutlinedInput-root": {
          height: 44,
          borderRadius: "6px",
          "& fieldset": { borderColor: "#D0D5DD" },
          "&:hover fieldset": { borderColor: "#9ca3af" },
          "&.Mui-focused fieldset": { borderColor: "#0226BE" },
        },
        "& .MuiOutlinedInput-input": {
          height: "auto",
          padding: "10px 14px",
        },
      }}
    />
  );
};

const resolveQuestion = (allQuestions, option) =>
  allQuestions?.find((question) => question?._id === option?.value) || {};

const OptionCheckbox = ({
  id,
  checked,
  onChange,
  label,
  required = false,
  compact = false,
}) => (
  <label
    htmlFor={id}
    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-md border-2 transition-all cursor-pointer ${
      checked
        ? "border-[#0226BE]/50 bg-blue-50/50"
        : "border-gray-200 hover:border-[#0226BE]/40 hover:bg-blue-50/30"
    } ${compact ? "py-2" : ""}`}
  >
    <input
      type="checkbox"
      id={id}
      checked={checked}
      required={required}
      onChange={onChange}
      className="w-5 h-5 border-2 border-gray-300 text-[#0226BE] focus:ring-2 focus:ring-[#0226BE] focus:ring-offset-1 rounded cursor-pointer"
    />
    <span className="text-[13px] font-[400] text-[#333] flex-1 select-none break-words">
      {label}
    </span>
  </label>
);

const FormActions = ({ loading, onCancel, submitLabel = "Set Qualification" }) => (
  <div className="w-full flex items-center justify-end gap-3 pt-2 border-t border-gray-200 mt-4">
    {loading ? (
      <CircularProgress className="text-[#0226BE]" size={24} />
    ) : (
      <>
        <button
          type="button"
          onClick={onCancel}
          className="px-5 py-2.5 text-[#666] text-[14px] font-[500] bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
        >
          Cancel
        </button>
        <button
          type="submit"
          className="px-6 py-2.5 text-white text-[14px] font-[500] bg-[#0226BE] rounded-md hover:bg-[#021ea8] transition-colors"
        >
          {submitLabel}
        </button>
      </>
    )}
  </div>
);

export const MultiPunch = ({
  allQuestions,
  edit,
  getTableQualification,
  data,
  id,
  getSingleSurvey,
  onClose,
  setSnackbar,
}) => {
  const [question, setQuestion] = useState(
    edit?.isEdit ? edit?.data?.question : {}
  );
  const [selectedQuestion, setSelectedQuestion] = useState({
    displayOption: edit?.isEdit ? edit?.data?.displayOptions : [],
    validAnswer: edit?.isEdit ? edit?.data?.validOptions : [],
  });
  const [condition] = useState({
    min: edit?.isEdit ? edit?.data?.condition?.min : "",
    max: edit?.isEdit ? edit?.data?.condition?.max : "",
  });
  const [loading, setLoading] = useState(false);
  const options = useMemo(
    () =>
      buildQuestionOptions(
        allQuestions,
        "Multi Punch",
        data?.qualification,
        edit?.isEdit
      ),
    [allQuestions, data?.qualification, edit?.isEdit]
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!question?._id) return;
    setLoading(true);
    const updatedQualification = [...(data?.qualification || [])];
    const indexToEdit = updatedQualification.findIndex(
      (fd) => fd._id === edit?.data?._id
    );
    if (indexToEdit !== -1) {
      updatedQualification[indexToEdit] = {
        question: question?._id,
        displayOptions: selectedQuestion?.displayOption,
        validOptions: selectedQuestion?.validAnswer,
        condition,
        _id: edit?.data?._id,
      };
    }
    try {
      await axios.put(`${BASE_URL}/survey?id=${id}`, {
        qualification: !edit?.isEdit
          ? [
              ...(data?.qualification || []),
              {
                question: question?._id,
                displayOptions: selectedQuestion?.displayOption,
                validOptions: selectedQuestion?.validAnswer,
                condition,
              },
            ]
          : updatedQualification,
      });
      setLoading(false);
      await getSingleSurvey();
      await getTableQualification();
      onClose();
      setSnackbar((prev) => ({
        ...prev,
        msg: !edit?.isEdit
          ? "Multi Punch Qualification Added !"
          : "Multi Punch Qualification Updated !",
        open: true,
        severity: "success",
      }));
    } catch (er) {
      setLoading(false);
      onClose();
      setSnackbar((prev) => ({
        ...prev,
        msg: "Something went wrong !",
        open: true,
        severity: "error",
      }));
    }
  };

  return (
    <form
      className="w-full px-5 pt-8 flex flex-col gap-[31px]"
      onSubmit={handleSubmit}
    >
      <div className="w-full flex flex-col gap-[20px] ">
        <div>
          <h1 className="text-[#000] text-[14px] font-[500] mb-1">
            Select Question
          </h1>
          <QuestionAutocomplete
            selectedQuestion={question}
            options={options}
            onSelect={(option) => {
              setQuestion(resolveQuestion(allQuestions, option));
              setSelectedQuestion({
                displayOption: [],
                validAnswer: [],
              });
            }}
          />
        </div>

        <div className="w-full border border-[#E0E0E0] rounded-md overflow-hidden">
          <div className="w-full px-5 py-2 flex items-center justify-between border-b border-[#E0E0E0] bg-gray-50">
            <h1 className="w-1/3 text-[14px] font-[500] text-[#000]">
              Display Options
            </h1>
            <h1 className="w-1/3 text-[14px] font-[500] text-[#000]">
              One or More From
            </h1>
            <h1 className="w-1/3 text-[14px] font-[500] text-[#000]">
              Selected Options
            </h1>
          </div>
          <div className="w-full px-5 py-3 flex gap-4 justify-between overflow-y-auto containerScroll max-h-[300px]">
            <div className="w-1/3 break-words flex flex-col gap-2">
              {question?._id ? (
                <OptionCheckbox
                  id="multi-display-select-all"
                  checked={
                    question?.options?.length ===
                    selectedQuestion?.displayOption?.length
                  }
                  label="Select All"
                  onChange={(e) => {
                    if (e.target.checked) {
                      setSelectedQuestion((prev) => ({
                        ...prev,
                        displayOption: question?.options || [],
                      }));
                    } else {
                      setSelectedQuestion({
                        displayOption: [],
                        validAnswer: [],
                      });
                    }
                  }}
                />
              ) : (
                <p className="text-[12px] text-[#888] py-2">
                  Choose a question to see options
                </p>
              )}
              {question?.options?.map((option, ind) => (
                <OptionCheckbox
                  key={`display-${option}-${ind}`}
                  id={`multi-display-${ind}`}
                  checked={selectedQuestion?.displayOption?.includes(option)}
                  required={!selectedQuestion?.displayOption?.length}
                  label={option}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setSelectedQuestion((prev) => ({
                        ...prev,
                        displayOption: [...prev?.displayOption, option],
                      }));
                    } else {
                      setSelectedQuestion((prev) => ({
                        ...prev,
                        displayOption: prev?.displayOption?.filter(
                          (item) => item !== option
                        ),
                        validAnswer: prev?.validAnswer?.filter(
                          (item) => item !== option
                        ),
                      }));
                    }
                  }}
                />
              ))}
            </div>
            <div className="w-1/3 break-words flex flex-col gap-2">
              {selectedQuestion?.displayOption?.length ? (
                <OptionCheckbox
                  id="multi-valid-select-all"
                  checked={
                    selectedQuestion?.displayOption?.length ===
                      selectedQuestion?.validAnswer?.length &&
                    selectedQuestion?.validAnswer?.length !== 0
                  }
                  label="Select All"
                  onChange={(e) => {
                    if (e.target.checked) {
                      setSelectedQuestion((prev) => ({
                        ...prev,
                        validAnswer: selectedQuestion?.displayOption,
                      }));
                    } else {
                      setSelectedQuestion((prev) => ({
                        ...prev,
                        validAnswer: [],
                      }));
                    }
                  }}
                />
              ) : (
                <p className="text-[12px] text-[#888] py-2">
                  Select display options first
                </p>
              )}
              {selectedQuestion?.displayOption?.map((option, ind) => (
                <OptionCheckbox
                  key={`valid-${option}-${ind}`}
                  id={`multi-valid-${ind}`}
                  checked={selectedQuestion?.validAnswer?.includes(option)}
                  required={!selectedQuestion?.validAnswer?.length}
                  label={option}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setSelectedQuestion((prev) => ({
                        ...prev,
                        validAnswer: [...prev?.validAnswer, option],
                      }));
                    } else {
                      setSelectedQuestion((prev) => ({
                        ...prev,
                        validAnswer: prev?.validAnswer?.filter(
                          (item) => item !== option
                        ),
                      }));
                    }
                  }}
                />
              ))}
            </div>
            <div className="w-1/3 break-words flex flex-col gap-2">
              {selectedQuestion?.validAnswer?.map((option, key) => (
                <h1
                  className="bg-[#0226BE26] text-[12px] text-[#0226BE] border border-[#0226BE] py-[4px] px-[8px] rounded-md w-fit"
                  key={key}
                >
                  {option}
                </h1>
              ))}
            </div>
          </div>
        </div>
      </div>
      <FormActions loading={loading} onCancel={onClose} />
    </form>
  );
};

export const SinglePunch = ({
  allQuestions,
  edit,
  getTableQualification,
  data,
  id,
  getSingleSurvey,
  onClose,
  setSnackbar,
}) => {
  const [question, setQuestion] = useState(
    edit?.isEdit ? edit?.data?.question : {}
  );
  const [selectedQuestion, setSelectedQuestion] = useState({
    displayOption: edit?.isEdit ? edit?.data?.displayOptions : [],
    validAnswer: edit?.isEdit ? edit?.data?.validOptions : [],
  });
  const [condition] = useState({
    min: edit?.isEdit ? edit?.data?.condition?.min : "",
    max: edit?.isEdit ? edit?.data?.condition?.max : "",
  });
  const [loading, setLoading] = useState(false);
  const options = useMemo(
    () =>
      buildQuestionOptions(
        allQuestions,
        "Single Punch",
        data?.qualification,
        edit?.isEdit
      ),
    [allQuestions, data?.qualification, edit?.isEdit]
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!question?._id) return;
    setLoading(true);
    const updatedQualification = [...(data?.qualification || [])];
    const indexToEdit = updatedQualification.findIndex(
      (fd) => fd._id === edit?.data?._id
    );
    if (indexToEdit !== -1) {
      updatedQualification[indexToEdit] = {
        question: question?._id,
        displayOptions: selectedQuestion?.displayOption,
        validOptions: selectedQuestion?.validAnswer,
        condition,
        _id: edit?.data?._id,
      };
    }
    try {
      await axios.put(`${BASE_URL}/survey?id=${id}`, {
        qualification: !edit?.isEdit
          ? [
              ...(data?.qualification || []),
              {
                question: question?._id,
                displayOptions: selectedQuestion?.displayOption,
                validOptions: selectedQuestion?.validAnswer,
                condition,
              },
            ]
          : updatedQualification,
      });
      setLoading(false);
      onClose();
      await getSingleSurvey();
      await getTableQualification();
      setSnackbar((prev) => ({
        ...prev,
        msg: !edit?.isEdit
          ? "Single Punch Qualification Added !"
          : "Single Punch Qualification Updated !",
        open: true,
        severity: "success",
      }));
    } catch (er) {
      setLoading(false);
      onClose();
      setSnackbar((prev) => ({
        ...prev,
        msg: "Something went wrong !",
        open: true,
        severity: "error",
      }));
    }
  };

  return (
    <form
      className="w-full px-5 pt-8 flex flex-col gap-[31px]"
      onSubmit={handleSubmit}
    >
      <div className="w-full flex flex-col gap-[20px] ">
        <div>
          <h1 className="text-[#000] text-[14px] font-[500] mb-1">
            Select Question
          </h1>
          <QuestionAutocomplete
            selectedQuestion={question}
            options={options}
            onSelect={(option) => {
              setQuestion(resolveQuestion(allQuestions, option));
              setSelectedQuestion({
                displayOption: [],
                validAnswer: [],
              });
            }}
          />
        </div>
        <div className="w-full border border-[#E0E0E0] rounded-md overflow-hidden">
          <div className="w-full px-5 py-2 flex items-center justify-between border-b border-[#E0E0E0] bg-gray-50">
            <h1 className="w-1/3 text-[14px] font-[500] text-[#000]">
              Display Options
            </h1>
            <h1 className="w-1/3 text-[14px] font-[500] text-[#000]">
              One or More From
            </h1>
            <h1 className="w-1/3 text-[14px] font-[500] text-[#000]">
              Selected Options
            </h1>
          </div>
          <div className="w-full px-5 py-3 flex gap-4 justify-between overflow-y-auto containerScroll max-h-[300px]">
            <div className="w-1/3 break-words flex flex-col gap-2">
              {question?._id ? (
                <OptionCheckbox
                  id="single-display-select-all"
                  checked={
                    question?.options?.length ===
                    selectedQuestion?.displayOption?.length
                  }
                  label="Select All"
                  onChange={(e) => {
                    if (e.target.checked) {
                      setSelectedQuestion((prev) => ({
                        ...prev,
                        displayOption: question?.options || [],
                      }));
                    } else {
                      setSelectedQuestion({
                        displayOption: [],
                        validAnswer: [],
                      });
                    }
                  }}
                />
              ) : (
                <p className="text-[12px] text-[#888] py-2">
                  Choose a question to see options
                </p>
              )}
              {question?.options?.map((option, key) => (
                <OptionCheckbox
                  key={`single-display-${option}-${key}`}
                  id={`single-display-${key}`}
                  checked={selectedQuestion?.displayOption?.includes(option)}
                  required={!selectedQuestion?.displayOption?.length}
                  label={option}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setSelectedQuestion((prev) => ({
                        ...prev,
                        displayOption: [...prev?.displayOption, option],
                      }));
                    } else {
                      setSelectedQuestion((prev) => ({
                        ...prev,
                        displayOption: prev?.displayOption?.filter(
                          (item) => item !== option
                        ),
                        validAnswer: prev?.validAnswer?.filter(
                          (item) => item !== option
                        ),
                      }));
                    }
                  }}
                />
              ))}
            </div>
            <div className="w-1/3 break-words flex flex-col gap-2">
              {selectedQuestion?.displayOption?.length ? (
                <OptionCheckbox
                  id="single-valid-select-all"
                  checked={
                    selectedQuestion?.displayOption?.length ===
                      selectedQuestion?.validAnswer?.length &&
                    selectedQuestion?.validAnswer?.length !== 0
                  }
                  label="Select All"
                  onChange={(e) => {
                    if (e.target.checked) {
                      setSelectedQuestion((prev) => ({
                        ...prev,
                        validAnswer: selectedQuestion?.displayOption,
                      }));
                    } else {
                      setSelectedQuestion((prev) => ({
                        ...prev,
                        validAnswer: [],
                      }));
                    }
                  }}
                />
              ) : (
                <p className="text-[12px] text-[#888] py-2">
                  Select display options first
                </p>
              )}
              {selectedQuestion?.displayOption?.map((option, ind) => (
                <OptionCheckbox
                  key={`single-valid-${option}-${ind}`}
                  id={`single-valid-${ind}`}
                  checked={selectedQuestion?.validAnswer?.includes(option)}
                  required={!selectedQuestion?.validAnswer?.length}
                  label={option}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setSelectedQuestion((prev) => ({
                        ...prev,
                        validAnswer: [...prev?.validAnswer, option],
                      }));
                    } else {
                      setSelectedQuestion((prev) => ({
                        ...prev,
                        validAnswer: prev?.validAnswer?.filter(
                          (item) => item !== option
                        ),
                      }));
                    }
                  }}
                />
              ))}
            </div>
            <div className="w-1/3 break-words flex flex-col gap-2">
              {selectedQuestion?.validAnswer?.map((option, validAnswerKey) => (
                <h1
                  className="bg-[#0226BE26] text-[12px] text-[#0226BE] border border-[#0226BE] py-[4px] px-[8px] rounded-md w-fit"
                  key={validAnswerKey}
                >
                  {option}
                </h1>
              ))}
            </div>
          </div>
        </div>
      </div>
      <FormActions loading={loading} onCancel={onClose} />
    </form>
  );
};

export const Numeric = ({
  allQuestions,
  edit,
  getTableQualification,
  data,
  id,
  getSingleSurvey,
  onClose,
  setSnackbar,
}) => {
  const [question, setQuestion] = useState(
    edit?.isEdit ? edit?.data?.question : {}
  );
  const [validOptions, setValidOptions] = useState(
    edit?.isEdit ? edit?.data?.validOptions : [{ from: "", to: "" }]
  );
  const [loading, setLoading] = useState(false);
  const options = useMemo(
    () =>
      buildQuestionOptions(
        allQuestions,
        "Numeric - Open-end",
        data?.qualification,
        edit?.isEdit
      ),
    [allQuestions, data?.qualification, edit?.isEdit]
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!question?._id) return;
    const updatedQualification = [...(data?.qualification || [])];
    const indexToEdit = updatedQualification.findIndex(
      (fd) => fd._id === edit?.data?._id
    );
    if (indexToEdit !== -1) {
      updatedQualification[indexToEdit] = {
        question: question?._id,
        validOptions: validOptions,
        _id: edit?.data?._id,
      };
    }
    try {
      setLoading(true);
      await axios.put(`${BASE_URL}/survey?id=${id}`, {
        qualification: !edit?.isEdit
          ? [
              ...(data?.qualification || []),
              {
                question: question?._id,
                validOptions: validOptions,
              },
            ]
          : updatedQualification,
      });
      setLoading(false);
      onClose();
      await getSingleSurvey();
      await getTableQualification();
      setSnackbar((prev) => ({
        ...prev,
        msg: !edit?.isEdit
          ? "Numeric Qualification Added !"
          : "Numeric Qualification Updated !",
        open: true,
        severity: "success",
      }));
    } catch (error) {
      setLoading(false);
      onClose();
      setSnackbar((prev) => ({
        ...prev,
        msg: "Something went wrong !",
        open: true,
        severity: "error",
      }));
    }
  };

  return (
    <form
      className="w-full px-5 pt-8 flex flex-col gap-[31px]"
      onSubmit={handleSubmit}
    >
      <div className="w-full flex flex-col gap-[20px] ">
        <div>
          <h1 className="text-[#000] text-[14px] font-[500] mb-1">
            Select Question
          </h1>
          <QuestionAutocomplete
            selectedQuestion={question}
            options={options}
            onSelect={(option) => {
              setValidOptions([{ from: "", to: "" }]);
              setQuestion(resolveQuestion(allQuestions, option));
            }}
          />
        </div>
        <div className="w-full ">
          <h1 className="text-[#000] text-[14px] font-[500]">
            Allow Responses
          </h1>
          <div className="w-[40%] flex gap-[19px]">
            <input
              type="number"
              placeholder="From"
              required
              value={validOptions?.[0]?.from}
              onChange={(e) => {
                const next = [...validOptions];
                next[0] = { ...next[0], from: Number(e.target.value) };
                setValidOptions(next);
              }}
              className="w-full border border-[#E0E0E0] rounded-[6.5px] pl-3 text-[#666] font-[400] text-[12px] h-[40px] mt-3 bg-[#F9F9FF]"
            />
            <input
              type="number"
              placeholder="To"
              required
              value={validOptions?.[0]?.to}
              onChange={(e) => {
                const next = [...validOptions];
                next[0] = { ...next[0], to: Number(e.target.value) };
                setValidOptions(next);
              }}
              className="w-full border border-[#E0E0E0] rounded-[6.5px] pl-3 text-[#666] font-[400] text-[12px] h-[40px] mt-3 bg-[#F9F9FF]"
            />
          </div>
        </div>
      </div>
      <FormActions loading={loading} onCancel={onClose} />
    </form>
  );
};

export const Text = ({
  allQuestions,
  edit,
  getTableQualification,
  data,
  id,
  getSingleSurvey,
  onClose,
  setSnackbar,
}) => {
  const [question, setQuestion] = useState(
    edit?.isEdit ? edit?.data?.question : {}
  );
  const [validOptions, setValidOptions] = useState(
    edit?.isEdit ? edit?.data?.validOptions?.[0] : ""
  );
  const [loading, setLoading] = useState(false);
  const options = useMemo(
    () =>
      buildQuestionOptions(
        allQuestions,
        "Text - Open-end",
        data?.qualification,
        edit?.isEdit
      ),
    [allQuestions, data?.qualification, edit?.isEdit]
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!question?._id) return;
    const updatedQualification = [...(data?.qualification || [])];
    const indexToEdit = updatedQualification.findIndex(
      (fd) => fd._id === edit?.data?._id
    );
    if (indexToEdit !== -1) {
      updatedQualification[indexToEdit] = {
        question: question?._id,
        validOptions: [validOptions],
        _id: edit?.data?._id,
      };
    }
    try {
      setLoading(true);
      await axios.put(`${BASE_URL}/survey?id=${id}`, {
        qualification: !edit?.isEdit
          ? [
              ...(data?.qualification || []),
              {
                question: question?._id,
                validOptions: [validOptions],
              },
            ]
          : updatedQualification,
      });
      setLoading(false);
      onClose();
      await getSingleSurvey();
      await getTableQualification();
      setSnackbar((prev) => ({
        ...prev,
        msg: !edit?.isEdit
          ? "Text Qualification Added !"
          : "Text Qualification Updated !",
        open: true,
        severity: "success",
      }));
    } catch (error) {
      setLoading(false);
      onClose();
      setSnackbar((prev) => ({
        ...prev,
        msg: "Something went wrong !",
        open: true,
        severity: "error",
      }));
    }
  };

  return (
    <form
      className="w-full px-5 pt-8 flex flex-col gap-[31px]"
      onSubmit={handleSubmit}
    >
      <div className="w-full flex flex-col gap-[40px] ">
        <div>
          <h1 className="text-[#000] text-[14px] font-[500] mb-1">
            Select Question
          </h1>
          <QuestionAutocomplete
            selectedQuestion={question}
            options={options}
            onSelect={(option) => {
              setQuestion(resolveQuestion(allQuestions, option));
              setValidOptions("");
            }}
          />
        </div>
        <div className="w-full ">
          <h1 className="text-[#000] text-[14px] font-[500]">
            Allow Responses
          </h1>
          <div className="w-full flex gap-[19px]">
            <input
              type="text"
              placeholder="Add Text"
              value={validOptions}
              onChange={(e) => setValidOptions(e.target.value)}
              className="w-full border border-[#E0E0E0] rounded-[6.5px] pl-3 text-[#666] font-[400] text-[12px] h-[40px] mt-3 bg-[#F9F9FF]"
            />
          </div>
        </div>
      </div>
      <FormActions loading={loading} onCancel={onClose} />
    </form>
  );
};
